// @ts-nocheck
import { action, computed, observable } from 'mobx'
import jwtDecode from 'jwt-decode'
import {
    createUrl,
	extractFirstAndLastName,
	reportClientError,
	formatLocalDateTime,
	getECommerceDomainByEnv,
	getStoreName,
	getTenantInfo,
	getTranslatedTextByKey,
	isMobileApp,
	sendRequest,
} from 'utils/utils'
import oStorage from 'utils/o-storage'
import { AUTHENTICATION_TYPE, SIGN_UP_STEPS, ECOMMERCE_USER, GUEST_USER } from 'constants/signUpProcess'
import { sendCustomEvent, setUser as setAnalyticUser } from 'utils/analytics/analytics'
import { enableBodyScroll } from 'body-scroll-lock'
import reactNativeComms from 'utils/reactNativeComms'
import type { UserIdentifier, AuthenticatorStatusKeys, HumanVerification } from './Account.type'
import { AUTH_QR_PATH } from './Account.constants'
import { IdentifierType } from '../Infra/Infra.type'
import type { AccessTokenBody } from '../../../utils/tokenHandler'
import type AddressManagerV2Store from 'mobx/AddressManagerV2/store'
import type CouponsStore from 'mobx/Coupons'
import { CONSTANTS, ORDER_TYPES } from 'utils/constants'

class Account {
	dependencies = {}

	constructor(dependencies) {
		this.dependencies = dependencies
	}

	@observable userExists = false

	@observable private identifier: UserIdentifier | null = null

	@observable language = 'ENG'

	@observable signUpStep = null

	@observable user: { user?: { email?: string; firstName?: string; lastName?: string } } | null = null

	@observable signUpPopUp = false

	@observable welcomAlreadyDisplayed = false

	@observable privacyPolicy = null

	@observable termsConditions = null

	@observable personalInfo: {
		firstName?: string
		lastName?: string
		email?: string
		phone?: string
	} = {}

	@observable verificationId = ''

	@observable signUpSkipped = false

	@observable userTokens = null

	@observable loggingOut = false

	@observable club = {
		clubName: 'Club',
		date: '2016',
	}

	@observable orderDetails = {
		location: 'Deliver to: 845 15th Street',
		time: '8:00 AM',
	}

	@observable fieldsAttributes = {}

	private onSignupPopupClose = null

	@observable signupTitle = undefined

	@observable signupSubTitle = ''

	@observable signupDisplaySkipSection = false

	@observable defaultCountry = 'US'

	@action
	setLoggingOut = (loggingOut) => {
		this.loggingOut = loggingOut
	}

	getIdentifier = () => this.identifier

	/**
	 * checks if the current accessToken which is held by the user is valid for further authorization with the server
	 * jwt's exp holds the number of seconds passed since January 1, 1970, UTC we multiply it by 1000 to align it
	 * with new Date() format in order to check if the expiration date has passed right now.
	 * @param user
	 * @returns {boolean}
	 */
	isTokenValid = (user) => {
		const { accessToken } = user
		const { exp } = jwtDecode(accessToken)
		const now = new Date()
		const expiredDate = new Date(exp * 1000)
		return expiredDate > now
	}

	/**
	 * each account or sign in call to the ecommerce server needs to be accompanied with authorization
	 * this function gets the most updated token and returns the authorization header if the user token is found and valid
	 * if it's not valid we refresh it
	 * if a user is not found no header is being returned
	 * @returns {Promise<{Authorization: string}|null>}
	 */
	getAuthorizationHeader = async (channelType) => {
		const user = oStorage.get(ECOMMERCE_USER)

		// If user signed in and access-token expired, refresh access-token
		if (user) {
			const { refreshToken: obsoleteRefreshToken, accessToken } = user
			if (obsoleteRefreshToken || !this.isTokenValid(user)) {
				try {
					const updatedToken = await this.refreshAccessToken(channelType, obsoleteRefreshToken)

					if (obsoleteRefreshToken) {
						delete user.refreshToken
						oStorage.set(ECOMMERCE_USER, user)
					}

					oStorage.set(ECOMMERCE_USER, { ...user, accessToken: updatedToken.accessToken })

					return { Authorization: `Bearer ${updatedToken.accessToken}` }
				} catch {
					oStorage.remove(ECOMMERCE_USER)
					this.user = null
					this.personalInfo = {}
					this.userExists = false
					window.location.redirect = '/home'
				}
			}

			return { Authorization: `Bearer ${accessToken}` }
		}

		// If anonymous, request a guest access-token (if not exist or expired)
		let guestUser = oStorage.get(GUEST_USER)
		if (!guestUser || !this.isTokenValid(guestUser)) {
			guestUser = await this.getAccessTokens(
				{
					authType: AUTHENTICATION_TYPE.GUEST,
				},
				channelType
			)

			oStorage.set(GUEST_USER, { ...guestUser })
		}

		return { Authorization: `Bearer ${guestUser.accessToken}` }
	}

	@observable communicationPrefs = {}

	@observable ExpandMoreDetails = {
		// kfcFood: {
		// 	label: 'KFC Food',
		// 	value: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
		// },
		support: {
			label: 'Support',
			value: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
		},
		legal: {
			label: 'Legal',
			value: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
		},
	}

	refreshAccessToken = async (channelType, obsoleteRefreshToken) => {
		const host = getECommerceDomainByEnv()
		const tenantId = getTenantInfo()

		return sendRequest(
			false,
			`/api/auth/refresh-token`,
			'post',
			{
				channelType,
			},
			{
				'tictuk-tenant-id': tenantId,
				'tictuk-wruec': host,
				...(obsoleteRefreshToken && { 'Tictuk-Refresh-Token': obsoleteRefreshToken }),
			},
			true,
			90000,
			null,
			true,
			{ withCredentials: true }
		)
	}

	getAccessTokens = async (body: AccessTokenBody, channelType: string) => {
		const host = getECommerceDomainByEnv()
		const tenantId = getTenantInfo()

		return sendRequest(
			false,
			`/api/auth/login`,
			'post',
			{ ...body, channelType },
			{
				'tictuk-tenant-id': tenantId,
				'tictuk-wruec': host,
			},
			true,
			90000,
			null,
			true,
			{ withCredentials: true }
		)
	}

	deleteUserRefreshToken = async () => {
		const host = getECommerceDomainByEnv()
		const tenantId = getTenantInfo()
		const user = this.getUser()
		if (user) {
			try {
				await sendRequest(
					false,
					`/api/auth/refresh-token`,
					'delete',
					null,
					{ 'tictuk-tenant-id': tenantId, 'tictuk-wruec': host },
					true,
					90000,
					null,
					true,
					{
						withCredentials: true,
					}
				)
			} catch (error: unknown) {
				console.error(error)
			}
		}
	}

	personalInfoUpdate = async (body, channelType) => {
		const host = getECommerceDomainByEnv()
		const tenantId = getTenantInfo()
		const authHeaders = await this.getAuthorizationHeader(channelType)
		await sendRequest(true, createUrl(host, `/v1/tenants/${tenantId}/users/me/accountSettings/personalInfo`), 'put', body, authHeaders)
	}

	accountSettings = async (channelType) => {
		const host = getECommerceDomainByEnv()
		const tenantId = getTenantInfo()
		const authHeaders = await this.getAuthorizationHeader(channelType)
		return sendRequest(false, createUrl(host, `/v1/tenants/${tenantId}/users/me/accountSettings`), 'get', null, authHeaders)
	}

	 
	verifyIdentifier = async (type: IdentifierType, value: string, humanVerification: HumanVerification) => {
		const host = getECommerceDomainByEnv()
		const tenantId = getTenantInfo()
        const deviceUniqueId = localStorage.getItem('deviceUniqueId')
		const response = await sendRequest(false, createUrl(host, `/v1/tenants/${tenantId}/auth/verifications`), 'post', {
			type,
			value,
			reCaptchaToken: humanVerification.reCaptchaToken,
			captchaSolution: humanVerification.friendlyCaptchaSolution,
			request: this.dependencies.getSessionId(),
            deviceUniqueId
		})

		return response
	}

	@action markUserAsSignUp = (User, store, addressManagerV2Store: AddressManagerV2Store, couponsStore: CouponsStore, locale: string) => {
		const currAction = this.user.userExists ? 'signin' : 'signup'
		const orderType = User.getOrderType() === CONSTANTS.DELIVERY_METHODS.DELIVERY ? ORDER_TYPES.DELIVERY : ORDER_TYPES.PEAKUP

		sendCustomEvent({
			category: 'account',
			action: currAction,
			label: 'success',
			loginMethod: this.identifier?.type,
			coupon_code: couponsStore.couponModal?.code || '',
			coupon_name: couponsStore.couponModal?.title[locale] || '',
			orderType,
			uuid: this.user?.user?.userConsistentId || '',
			phone_number: this.user?.user?.phone || '',
			email: this.user?.user?.email || '',
			first_name: this.user?.user?.firstName || '',
			last_name: this.user?.user?.lastName || '',
			storeID: store?.data?.id || '',
			storeName: getStoreName(store, '', '', addressManagerV2Store) || '',
			page_path: window.location.pathname,
			page_location: window.location.href,
			marketingEmailsOptIn: this.communicationPrefs.marketingEmailsOptIn?.value || false,
			editorsPickAndCustomersFavoriteOptIn: this.communicationPrefs.editorsPickAndCustomersFavoriteOptIn?.value || false,
			newsAndAccouncementsOptIn: this.communicationPrefs.newsAndAccouncementsOptIn?.value || false,
			canSellMyPersonalInfoOptIn: this.communicationPrefs.canSellMyPersonalInfoOptIn?.value || false
		})
	}

	@action setDefaultCountry = (region) => {
		// defaultCountry is an ISO 3166-1 country code, e.g. 'US', 'MX'
		this.defaultCountry = region
	}

	isPushNotificationDeclined = () => this.dependencies.isPushNotificationDeclined()

	getPushNotificationsStatus = () => this.dependencies.getPushNotificationsStatus()

	// This method is called to check the push notification status and open the modal to ask the user to grant them
	@action checkPushNotificationsStatus = (isPushNotificationSupported) => {
		if (!isPushNotificationSupported) {
			return false
		}

		if (!isMobileApp()) {
			return false
		}

		const doesTokenExist = this.getPushNotificationsStatus() === 'accepted'

		if (doesTokenExist) {
			return false
		}

		if (!this.isPushNotificationDeclined()) {
			return false
		}

		return true
	}

	@action checkConsistentIdAndPushNotificationsStatus = async (User, userConsistentId, addressManagerV2Store: AddressManagerV2Store) => {
		const userConsistentIdChanged = User.getUserConsistentId() !== userConsistentId
		User.setUserConsistentId(userConsistentId)

		if (userConsistentIdChanged) {
			const orderType = addressManagerV2Store.formattedOrderTypeForSession()
			try {
				await this.dependencies.initOrUpdateSession({ refObject: { orderType }, shouldRedirectIfError: false })
			} catch (error: unknown) {
				console.error(error)
			}

			if (this.getPushNotificationsStatus() === 'accepted') {
				reactNativeComms.sendMessage.askPushNotifications()
			}
		}
	}

	@action getUserTokens = () => this.userTokens

	@action verifyCode = async (code) => {
		try {
			this.userTokens = await this.getAccessTokens({
				authType: AUTHENTICATION_TYPE.VERIFICATION_CODE,
				verificationId: this.verificationId, // Comes from response of the previous request to /phone-verifications
				verificationCode: code, // The code which the user entered
				request: this.dependencies.getSessionId(),
			})

			return this.userTokens
		} catch (error) {
			console.error(error)
			throw error
		}
	}

	@action updateUserTokensEmail = (email) => {
		if(this.userTokens.user) {
			this.userTokens.user = {
				...this.userTokens.user,
				email
			}
		}
	}

	@action login = async (User, store, channelType, addressManagerV2Store: AddressManagerV2Store, couponsStore: CouponsStore, locale: string) => {
		try {
			this.userExists = true
			await this.storeUser(channelType, this.userTokens)

			const userConsistentId = this.userTokens.user?.userConsistentId
			if (userConsistentId) {
				setAnalyticUser(userConsistentId)
				if (isMobileApp()) {
					reactNativeComms.sendMessage.setCUID(userConsistentId, this.userTokens?.user?.email || '')
					this.checkConsistentIdAndPushNotificationsStatus(User, userConsistentId, addressManagerV2Store)
				}
			}

			this.markUserAsSignUp(User, store, addressManagerV2Store, couponsStore, locale)
		} catch (error) {
			console.error(error)
			throw error
		}
	}

	// TODO: Check!!! Used only in this file and maybe can be just a regular function and not an action
	@action storeUser = async (channelType, data) => {
		const alreadyStoredUser = oStorage.get(ECOMMERCE_USER) || {}
		const updatedUser = { ...alreadyStoredUser, ...data }

		oStorage.set(ECOMMERCE_USER, updatedUser)

		this.user = updatedUser
		await this.getAccountSettings(channelType)
	}

	@action storeUserDetails = async (channelType, fullName, email = null, phoneNumber = null) => {
		const [firstName, lastName] = extractFirstAndLastName(fullName)
		const userInfoForAPI = {
			email: this.identifier?.type === IdentifierType.Email ? this.identifier?.value : email,
			phone: this.identifier?.type === IdentifierType.Phone ? this.identifier?.value : phoneNumber,
			firstName,
			lastName,
		}

		const userInfo = {
			...this.user,
			user: {
				...this.user.user,
				...userInfoForAPI,
			},
		}

		const userConsistentId = this.user?.user?.userConsistentId
		if (userConsistentId) {
			setAnalyticUser(userConsistentId, firstName, lastName, fullName, email, phoneNumber)
			if (isMobileApp()) {
				reactNativeComms.sendMessage.setCUID(userConsistentId, email || '')
			}
		}

		await this.personalInfoUpdate(userInfoForAPI, channelType)

		await this.storeUser(channelType, userInfo)
		this.openSignUpPopUp(false)
	}

	@action skip = (locale, store, addressManagerV2Store: AddressManagerV2Store) => {
		this.signUpSkipped = true

		const _localDateTime = formatLocalDateTime(locale)

		sendCustomEvent({
			category: 'account',
			action: 'signup',
			label: 'skip',
			loginMethod: this.getIdentifier()?.type,
			date: _localDateTime,
			storeID: store?.data?.id || '',
			storeName: getStoreName(store, '', '', addressManagerV2Store) || '',
		})
	}

	@action moveToSignUpStep = (step) => {
		this.signUpStep = step
	}

	@action getAccountSettings = async (channelType) => {
		const user = await this.accountSettings(channelType)
		this.personalInfo = user.personalInfo

		if (user.editAccountFormAttributes?.fields) {
			this.fieldsAttributes = user.editAccountFormAttributes.fields.reduce((acc, curr) => {
				acc[curr.name] = curr.attributes
				return acc
			}, {})
		}

		if (this.personalInfo.phone === '0') {
			this.personalInfo.phone = ''
		}

		const defaultCommunicationPrefs = {
			marketingEmailsOptIn: {
				label: getTranslatedTextByKey('eCommerce.accountSettings.keepMeUpToDate'),
				note: '',
				value: false,
				showToUser: true,
			},
			editorsPickAndCustomersFavoriteOptIn: {
				label: getTranslatedTextByKey('eCommerce.accountSettings.editorsPickAndCustomerFavorite'),
				note: getTranslatedTextByKey('eCommerce.accountSettings.reveiceEditorUpdates'),
				value: false,
				showToUser: false,
			},
			newsAndAccouncementsOptIn: {
				label: getTranslatedTextByKey('eCommerce.accountSettings.newsAndAnnouncements'),
				note: getTranslatedTextByKey('eCommerce.accountSettings.getEmailsAboutSiteImprovements'),
				value: false,
				showToUser: false,
			},
			canSellMyPersonalInfoOptIn: {
				label: getTranslatedTextByKey('eCommerce.accountSettings.doNotSellMyInformation'),
				note: '',
				value: false,
				showToUser: false,
			},
		}

		let commPrefs = {}

		Object.entries(user.communicationPrefs).forEach(([key, value]) => {
			commPrefs = {
				...commPrefs,
				[key]: {
					label: defaultCommunicationPrefs[key].label,
					note: defaultCommunicationPrefs[key].note,
					value,
					showToUser: defaultCommunicationPrefs[key].showToUser,
				},
			}
		})
		this.communicationPrefs = commPrefs
	}

	@action isAuthenticated = () => !!Object.keys(this.getUser()?.user ?? {}).length

	@action onCommunicationPrefsChange = (valueKeyPair) => {
		this.communicationPrefs = {
			...this.communicationPrefs,
			...valueKeyPair,
		}
	}

	@action sendMeCode = async (identifierType: IdentifierType, identifierValue: string, humanVerification: HumanVerification) => {
		const { id } = await this.verifyIdentifier(identifierType, identifierValue, humanVerification)

		this.identifier = { type: identifierType, value: identifierValue }
		this.verificationId = id
		this.moveToSignUpStep(SIGN_UP_STEPS.EDIT_CODE)
	}

	@action getUser = () => {
		if (!this.user) {
			const storedUser = oStorage.get(ECOMMERCE_USER)
			if (storedUser) {
				this.user = storedUser
				this.userExists = true
			}
		}

		return this.user
	}

	@computed get userName() {
		const _user = this.user
		return _user?.user?.firstName || _user?.user?.lastName || getTranslatedTextByKey('eCommerce.signIn.anonymous')
	}

	@action openSignUpPopUp = async (signUpPopUp, props) => {
		const { onSignupPopupClose, signupTitle, signupSubTitle, signupDisplaySkipSection } = props || {}

		if (onSignupPopupClose) {
			this.onSignupPopupClose = onSignupPopupClose
		}

		if (signUpPopUp) {
			this.moveToSignUpStep(SIGN_UP_STEPS.VERIFY_IDENTIFIER)
		} else {
			const targetElement = document.getElementById('signUpContainer')
			if (targetElement) {
				enableBodyScroll(targetElement)
			}

			if (this.onSignupPopupClose) {
				try {
					const callback = this.onSignupPopupClose

					this.onSignupPopupClose = null
					await callback()
				} catch (error) {
					console.error(error)
				}
			}

			// reset the signup step to stop firing an analytics event
			this.moveToSignUpStep(null)
		}

		this.signUpPopUp = signUpPopUp
		this.identifier = {}
		this.signupTitle = signupTitle
		this.signupSubTitle = signupSubTitle
		this.signupDisplaySkipSection = signupDisplaySkipSection ?? true
	}

	@action saveCommunicationPrefs = async (channelType) => {
		try {
			const authHeaders = await this.getAuthorizationHeader(channelType)
			if (authHeaders) {
				const tenantId = getTenantInfo()
				const host = getECommerceDomainByEnv()
				const body = Object.entries(this.communicationPrefs).reduce((obj, [key, { value }]) => ({ ...obj, [key]: value }), {})
				await sendRequest(true, createUrl(host, `/v1/tenants/${tenantId}/users/me/accountSettings/communicationPrefs`), 'put', body, authHeaders)
			
				sendCustomEvent({
					category: 'account',
					action: 'save communication prefs',
					label: 'success',
					uuid: this.user?.user?.userConsistentId || '',
					phone_number: this.user?.user?.phone || '',
					email: this.user?.user?.email || '',
					first_name: this.user?.user?.firstName || '',
					last_name: this.user?.user?.lastName || '',
					marketingEmailsOptIn: this.communicationPrefs.marketingEmailsOptIn?.value || false,
					editorsPickAndCustomersFavoriteOptIn: this.communicationPrefs.editorsPickAndCustomersFavoriteOptIn?.value || false,
					newsAndAccouncementsOptIn: this.communicationPrefs.newsAndAccouncementsOptIn?.value || false,
					canSellMyPersonalInfoOptIn: this.communicationPrefs.canSellMyPersonalInfoOptIn?.value || false
				})
			
			} else {
				console.error('No authorization for this action')
				window.location.redirect = '/home'
			}
		} catch (error) {
			reportClientError(error, 'Account.saveCommunicationPrefs')
		}
	}

	@action leaveClub = () => {
		this.club.clubName = ''
		this.club.date = ''
	}

	@action personalInfoChanged = (valueKeyPair) => {
		this.personalInfo = {
			...this.personalInfo,
			...valueKeyPair,
		}
	}

	@action deleteAccount = async (channelType) => {
		try {
			await this.deleteUserRefreshToken()
			const authHeaders = await this.getAuthorizationHeader(channelType)
			if (authHeaders) {
				const tenantId = getTenantInfo()
				const host = getECommerceDomainByEnv()
				const deleteUserRes = await sendRequest(true, createUrl(host, `/v1/tenants/${tenantId}/users/me`), 'delete', null, authHeaders)
				if (!deleteUserRes.error) {
					this.dependencies.resetLocalStorage()

					this.user = null
					this.personalInfo = {}
					this.userExists = false

					this.dependencies.resetAnalyticUser()
				}
			} else {
				console.error('No authorization for this action')
				window.location.redirect = '/home'
			}
		} catch (error) {
			reportClientError(error, 'Account.deleteAccount')
		}
	}

	@action signedOut = async (setBackdropEnabled, setClickedFeaturedItem) => {
		try {
			await this.deleteUserRefreshToken() // ADD APIS TO SIGNED OUT
			this.dependencies.resetLocalStorage()

			this.user = null
			this.userExists = false
			this.personalInfo = {}
			setBackdropEnabled(false)
			setClickedFeaturedItem(null)

			this.dependencies.resetAnalyticUser()
		} catch (error) {
			console.error(error)
		}
	}

	@action setLanguage = (language) => {
		this.language = language
	}

	@action setTermsConditions = (href) => {
		this.termsConditions = href
	}

	@action setPrivacyPolicy = (href) => {
		this.privacyPolicy = href
	}

	@action setWelcomAlreadyDisplayed = (welcomAlreadyDisplayed) => {
		this.welcomAlreadyDisplayed = welcomAlreadyDisplayed
	}

	getAuthenticatorCode = async (channelType: string, deviceType = 'kiosk'): Promise<{ code: string } & AuthenticatorStatusKeys> => {
		const authHeaders = await this.getAuthorizationHeader(channelType)
		const headers = {
			...authHeaders,
			'tictuk-tenant-id': getTenantInfo(),
		}

		if (authHeaders) {
			const host = getECommerceDomainByEnv()
			const result = await sendRequest(false, createUrl(host, `/v3/auth/sync/${deviceType}`), 'post', null, headers)

			return result
			/* return new Promise((resolve) => {
				setTimeout(() => {
					resolve(result)
				}, 4000)
			}) */
		}
	}

	getAuthenticatorStatus = async (channelType: string, keys: AuthenticatorStatusKeys, deviceType = 'kiosk'): Promise<string> => {
		const host = getECommerceDomainByEnv()
		const authHeaders = await this.getAuthorizationHeader(channelType)
		const headers = {
			...authHeaders,
			'tictuk-tenant-id': getTenantInfo(),
		}

		if (authHeaders) {
			const response = await sendRequest(false, createUrl(host, `/v3/auth/sync/${deviceType}/status`), 'post', keys, headers)

			return response.result
			/* return new Promise((resolve) => {
				setTimeout(() => {
					resolve(response.result)
				}, 4000)
			}) */
		}
	}

	patchQRLogin = async (
		channelType: string,
		qrToken: string,
	): Promise<{ ok: true; keys: AuthenticatorStatusKeys } | { ok: false; status: number }> => {
		const host = getECommerceDomainByEnv()
		const authHeaders = await this.getAuthorizationHeader(channelType)
		if (!authHeaders) {
			return { ok: false, status: 401 }
		}
		const headers = {
			...authHeaders,
			'tictuk-tenant-id': getTenantInfo(),
		}

		try {
			const response = await sendRequest(
				false,
				createUrl(host, `${AUTH_QR_PATH}/${encodeURIComponent(qrToken)}`),
				'patch',
				null,
				headers,
			)
			return { ok: true, keys: { waitingKey: response.waitingKey, connectedKey: response.connectedKey } }
		} catch (err: unknown) {
			const status = (err as { response?: { status?: number } })?.response?.status
				?? (err as { status?: number })?.status
				?? 0
			return { ok: false, status }
		}
	}

	disconnectAuthenticator = async (channelType: string, keys: AuthenticatorStatusKeys, deviceType = 'kiosk'): Promise<any> => {
		const host = getECommerceDomainByEnv()
		const authHeaders = await this.getAuthorizationHeader(channelType)
		const headers = {
			...authHeaders,
			'tictuk-tenant-id': getTenantInfo(),
		}

		if (authHeaders) {
			const response = await sendRequest(
				false,
				createUrl(host, `/v3/auth/sync/${deviceType}/disconnect`),
				'post',
				{
					key: keys.connectedKey,
				},
				headers
			)

			return response
			/* return new Promise((resolve) => {
				setTimeout(() => {
					resolve(response.result)
				}, 2000)
			}) */
		}
	}
}

export default Account
